// public/js/base/baseController.js

import { createLogger } from '@utils/logger.js';
import AppSettings from '@services/appSettings.js';
import baseMessages from './baseMessages.js';

/**
 * BaseController - Schema-driven business logic + event handling
 * Handles: Event listeners, API calls, business workflows, navigation
 * Uses Manager schema to auto-generate form collection, validation, etc.
 * Uses Manager for data operations and Views for UI generation
 * NOW SUPPORTS: Configurable filtering (real-time vs apply button) + BaseMessages system
 */
class BaseController {
    constructor(config = {}) {
        if (this.constructor.instance) return this.constructor.instance;
        this.constructor.instance = this;

        this.logger = createLogger('BaseController');
        
        this.manager = config.manager;
        this.views = config.views;
        this.api = config.api;
        this.overlay = config.overlay;
        this.parent = config.parent || null; // Parent reference (usually CoreUiComponents)

        this.entityName = config.entityName || 'entity';
        this.entityNamePlural = config.entityNamePlural || `${this.entityName}s`;

        this.current_entity_id = null;
        this.loadingStates = new Map();
        
        this.errorHandler = config.errorHandler || this.defaultErrorHandler.bind(this);
        
        // Filter state management
        this.filterState = {
            active: false,
            values: {},
            filteredResults: null,
            lastFilterTime: null
        };
        
        // Get filter settings for this entity type
        this.filterSettings = AppSettings.getFilterSettings(this.entityName);
        
        this.logger.info(`Controller initialized for ${this.entityName}`, {
            hasManager: !!this.manager,
            hasViews: !!this.views,
            hasApi: !!this.api,
            hasOverlay: !!this.overlay,
            hasParent: !!this.parent,
            filterMode: this.filterSettings.realtime ? 'real-time' : 'apply-button',
            persistent: this.filterSettings.persistent
        });
        
        this.createEventListeners();
    }

    // ===============================
    // SCHEMA-DRIVEN CONFIGURATION
    // ===============================

    /**
     * Get editable fields for this entity (override in subclasses if needed)
     * By default, returns all form fields from schema
     * @returns {Array} Array of field names that can be edited
     */
    getEditableFields() {
        return this.manager.getFormFields();
    }

    /**
     * Get required fields for this entity (from schema)
     * @returns {Array} Array of required field names
     */
    getRequiredFields() {
        return this.manager.getRequiredFields();
    }

    /**
     * Get filter fields configuration (from schema)
     * @returns {Array} Array of filter field configurations
     */
    getFilterFields() {
        return this.manager.generateFilterConfig();
    }

    // ===============================
    // FILTER STATE MANAGEMENT
    // ===============================

    /**
     * Check if any filters are currently active
     * @returns {boolean} True if filters are active
     */
    hasActiveFilters() {
        return Object.values(this.filterState.values).some(value => 
            value !== null && value !== undefined && value !== ''
        );
    }

    /**
     * Get current filter values
     * @returns {Object} Current filter values
     */
    getActiveFilters() {
        const activeFilters = {};
        Object.entries(this.filterState.values).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                activeFilters[key] = value;
            }
        });
        return activeFilters;
    }

    /**
     * Clear all filters
     */
    clearAllFilters() {
        this.logger.debug('Clearing all filters');

        this.filterState.values = {};
        this.filterState.active = false;
        this.filterState.filteredResults = null;

        // Clear UI filter inputs
        const filterContainer = document.querySelector('.entity-filter-container');
        if (filterContainer) {
            const inputs = filterContainer.querySelectorAll('input, select');
            inputs.forEach(input => {
                input.value = '';
                input.classList.remove('filter-active');
            });
        }

        // Clear filter tags
        this.updateFilterTags();

        // Update filter indicators
        this.updateFilterIndicators();

        // Refresh display with all entities
        this.displayFilteredResults(this.manager.read());
    }

    /**
     * Remove a single filter
     * @param {string} filterName - Name of the filter to remove
     */
    removeFilter(filterName) {
        this.logger.debug(`Removing filter: ${filterName}`);

        // Remove from state
        delete this.filterState.values[filterName];

        // Clear UI input
        const filterContainer = document.querySelector('.entity-filter-container');
        if (filterContainer) {
            const input = filterContainer.querySelector(`[name="${filterName}"]`);
            if (input) {
                input.value = '';
                input.classList.remove('filter-active');
            }
        }

        // Update tags
        this.updateFilterTags();

        // Apply filters
        const filteredEntities = this.applyFilters();
        this.displayFilteredResults(filteredEntities);
        this.updateFilterIndicators();
    }

    /**
     * Toggle filters collapse/expand
     */
    toggleFiltersCollapse() {
        const filterContainer = document.querySelector('.entity-filter-container');
        if (!filterContainer) return;

        const isCollapsed = filterContainer.classList.contains('collapsed');
        const toggleButton = document.getElementById('filter-toggle-btn');
        const chevronIcon = toggleButton?.querySelector('.material-symbols-outlined');

        if (isCollapsed) {
            // Expand
            filterContainer.classList.remove('collapsed');
            if (chevronIcon) {
                chevronIcon.textContent = this.views.iconMap.collapse;
            }
            this.logger.debug('Filters expanded');
        } else {
            // Collapse
            filterContainer.classList.add('collapsed');
            if (chevronIcon) {
                chevronIcon.textContent = this.views.iconMap.expand;
            }
            this.logger.debug('Filters collapsed');
        }
    }

    /**
     * Update filter tags display
     */
    updateFilterTags() {
        const tagsContainer = document.querySelector('.filter-tags-container');
        if (!tagsContainer) return;

        // Clear existing tags
        tagsContainer.innerHTML = '';

        const activeFilters = this.getActiveFilters();
        const filterCount = Object.keys(activeFilters).length;

        // Hide container if no filters
        if (filterCount === 0) {
            tagsContainer.style.display = 'none';
            return;
        }

        // Show container
        tagsContainer.style.display = 'flex';

        // Create tag for each active filter
        Object.entries(activeFilters).forEach(([filterName, filterValue]) => {
            const tag = document.createElement('div');
            tag.className = 'filter-tag';
            tag.dataset.filterName = filterName;

            const label = this.getFilterDisplayName(filterName);
            const displayValue = this.formatFilterValue(filterValue);

            tag.innerHTML = `
                <span class="filter-tag-label">${label}: ${displayValue}</span>
                <span class="filter-tag-remove material-symbols-outlined">close</span>
            `;

            tagsContainer.appendChild(tag);
        });

        this.logger.debug(`Updated filter tags: ${filterCount} active`);
    }

    /**
     * Format filter value for display
     * @param {*} value - Filter value
     * @returns {string} Formatted value
     */
    formatFilterValue(value) {
        if (value === null || value === undefined) return '';

        // Truncate long strings
        const strValue = String(value);
        if (strValue.length > 30) {
            return strValue.substring(0, 27) + '...';
        }

        return strValue;
    }

    /**
     * Apply current filters to the dataset - IMPROVED for multiple date ranges
     * @returns {Array} Filtered entities
     */
    applyFilters() {
        const allEntities = this.manager.read();
        const activeFilters = this.getActiveFilters();

        if (Object.keys(activeFilters).length === 0) {
            this.filterState.active = false;
            this.filterState.filteredResults = null;
            return allEntities;
        }

        const filteredEntities = allEntities.filter(entity => {
            // Check all filters
            return Object.entries(activeFilters).every(([filterName, filterValue]) => {
                // Handle date range filters (fieldName_start, fieldName_end)
                if (filterName.endsWith('_start') || filterName.endsWith('_end')) {
                    const isStartFilter = filterName.endsWith('_start');
                    const baseFieldName = filterName.replace(/_start$|_end$/, '');

                    // Check if this is a date field
                    const fieldSchema = this.manager.getFieldSchema(baseFieldName);
                    if (fieldSchema && (fieldSchema.type === 'date' || fieldSchema.type === 'datetime-local')) {
                        return this.matchesDateRangeFilter(entity, baseFieldName, filterName, filterValue, isStartFilter);
                    }
                }

                // Handle all other filters
                return this.matchesFilter(entity, filterName, filterValue);
            });
        });

        this.filterState.active = true;
        this.filterState.filteredResults = filteredEntities;
        return filteredEntities;
    }

    /**
     * Check if entity matches a date range filter
     * @param {Object} entity - Entity to check
     * @param {string} baseFieldName - Base field name (e.g., 'activity_date_start')
     * @param {string} filterName - Full filter name (e.g., 'activity_date_start_start')
     * @param {*} filterValue - Filter value (date string)
     * @param {boolean} isStartFilter - True if this is a start filter, false for end filter
     * @returns {boolean} True if entity matches filter
     */
    matchesDateRangeFilter(entity, baseFieldName, filterName, filterValue, isStartFilter) {
        if (!filterValue) return true;

        const entityDateValue = entity[baseFieldName];
        if (!entityDateValue) return false;

        try {
            const entityDate = new Date(entityDateValue);
            const filterDate = new Date(filterValue);

            if (isStartFilter) {
                // For start filter: entity date must be >= filter date
                return entityDate >= filterDate;
            } else {
                // For end filter: entity date must be <= filter date
                return entityDate <= filterDate;
            }
        } catch (error) {
            this.logger.warn(`Failed to parse date for filter ${filterName}:`, error);
            return false;
        }
    }

    /**
     * Check if entity matches a specific filter
     * @param {Object} entity - Entity to check
     * @param {string} filterName - Filter field name
     * @param {*} filterValue - Filter value
     * @returns {boolean} True if entity matches filter
     */
    matchesFilter(entity, filterName, filterValue) {
        if (filterValue === null || filterValue === undefined || filterValue === '') {
            return true;
        }

        // Handle search filter
        if (filterName === 'search') {
            const searchFields = ['title', 'description', 'location', 'name', 'nick_name'];
            const searchTerm = filterValue.toLowerCase();
            return searchFields.some(field => {
                const fieldValue = entity[field];
                return fieldValue && fieldValue.toLowerCase().includes(searchTerm);
            });
        }

        // Get field schema for type-specific handling
        const fieldSchema = this.manager.getFieldSchema(filterName);

        if (fieldSchema) {
            // Handle relationship fields with partial text search
            if (fieldSchema.type === 'relationship') {
                const entityIdValue = entity[filterName];
                const entityNameValue = entity[`${filterName}_name`];
                const searchTerm = filterValue.toLowerCase();

                // Check both ID match and partial name match
                if (entityIdValue === filterValue) return true;
                if (entityNameValue && entityNameValue.toLowerCase().includes(searchTerm)) return true;

                return false;
            }

            // Handle checkbox fields
            if (fieldSchema.type === 'checkbox') {
                const entityValue = entity[filterName];
                const booleanFilterValue = filterValue === 'true' || filterValue === true;
                return entityValue === booleanFilterValue;
            }

            // Handle select fields with exact matching
            if (fieldSchema.type === 'select') {
                return entity[filterName] === filterValue;
            }
        }

        // Default: substring matching for strings, exact matching for others
        const entityValue = entity[filterName];

        if (typeof entityValue === 'string' && typeof filterValue === 'string') {
            return entityValue.toLowerCase().includes(filterValue.toLowerCase());
        }

        return entityValue === filterValue;
    }

    entityOverlapsDateRange(entity, startDate, endDate) {
        // Auto-detect date fields in entity
        const entityStartDate = entity.start_date || entity.date_start || entity.date_create;
        const entityEndDate = entity.end_date || entity.date_end || entityStartDate;
        
        if (!entityStartDate) return false;
        
        try {
            const entStart = new Date(entityStartDate);
            const entEnd = entityEndDate ? new Date(entityEndDate) : entStart;
            
            if (startDate && !endDate) {
                const filterStart = new Date(startDate);
                return entStart >= filterStart || entEnd >= filterStart;
            }
            
            if (!startDate && endDate) {
                const filterEnd = new Date(endDate);
                return entStart <= filterEnd || entEnd <= filterEnd;
            }
            
            if (startDate && endDate) {
                const filterStart = new Date(startDate);
                const filterEnd = new Date(endDate);
                return entStart <= filterEnd && entEnd >= filterStart;
            }
            
            return true;
        } catch (error) {
            return false;
        }
    }

    /**
     * Update filter UI indicators
     */
    updateFilterIndicators() {
        const activeFilters = this.getActiveFilters();
        const filterContainer = document.querySelector('.entity-filter-container');
        
        if (!filterContainer) return;
        
        // Update individual field indicators
        if (AppSettings.ui.highlightActiveFields) {
            const inputs = filterContainer.querySelectorAll('input, select');
            inputs.forEach(input => {
                const filterName = input.name || input.id.replace(`${this.entityName}-`, '').replace('-filter', '');
                const hasValue = activeFilters.hasOwnProperty(filterName);
                input.classList.toggle('filter-active', hasValue);
            });
        }

        // Update apply button state
        this.updateApplyButtonState();
    }

    /**
     * Get display name for filter field
     * @param {string} filterName - Filter field name
     * @returns {string} Display name
     */
    getFilterDisplayName(filterName) {
        const standardFilterNames = {
            'search': 'Search',
            'start_date': 'Start Date',
            'end_date': 'End Date'
        };
        
        if (standardFilterNames[filterName]) {
            return standardFilterNames[filterName];
        }
        
        return this.manager.getFieldLabel(filterName);
    }

    /**
     * Update apply button state
     */
    updateApplyButtonState() {
        const applyButton = document.querySelector('.apply-filters-button');
        if (!applyButton) return;
        
        const hasFilters = this.hasActiveFilters();
        applyButton.disabled = !hasFilters;
        applyButton.classList.toggle('has-filters', hasFilters);
    }

    /**
     * Display filtered results
     * @param {Array} entities - Entities to display
     */
    displayFilteredResults(entities) {
        this.logger.ui(`Displaying ${entities.length} filtered results`);
        
        // Try different possible grid IDs for flexibility
        const possibleGridIds = [
            `${this.entityName}-cards-grid`,
            `${this.entityName}-cards-container`,
            `${this.entityName}s-container`
        ];
        
        let cardsGrid = null;
        for (const id of possibleGridIds) {
            cardsGrid = document.getElementById(id);
            if (cardsGrid) {
                this.logger.debug(`Found cards grid with ID: ${id}`);
                break;
            }
        }
        
        if (!cardsGrid) {
            this.logger.error(`No cards grid found for ${this.entityName}. Tried IDs:`, possibleGridIds);
            return;
        }
        
        // Clear existing cards (except new entity card) - try multiple selectors
        const possibleCardSelectors = [
            `.${this.entityName}-card:not(.${this.entityName}-card-new-${this.entityName})`,
            `.${this.entityName}-card:not(.${this.entityName}-card-new)`,
            `.${this.entityName}-card:not(.entity-card-new)`
        ];
        
        let existingCards = [];
        for (const selector of possibleCardSelectors) {
            existingCards = cardsGrid.querySelectorAll(selector);
            if (existingCards.length > 0) {
                this.logger.debug(`Found existing cards with selector: ${selector}`);
                break;
            }
        }
        
        existingCards.forEach(card => card.remove());
        
        // Create cards for filtered entities
        entities.forEach(entity => {
            const cardData = this.prepareCardData(entity);
            const card = this.createEntityCard ? this.createEntityCard(cardData) : this.views.createEntityCard(cardData);
            
            // Try to find new entity card with multiple selectors
            const possibleNewCardSelectors = [
                `.${this.entityName}-card-new-${this.entityName}`,
                `.${this.entityName}-card-new`,
                `.entity-card-new`,
                `.${this.entityName}-new-entity-button`
            ];
            
            let newCard = null;
            for (const selector of possibleNewCardSelectors) {
                newCard = cardsGrid.querySelector(selector);
                if (newCard) {
                    this.logger.debug(`Found new entity card with selector: ${selector}`);
                    break;
                }
            }
            
            if (newCard) {
                cardsGrid.insertBefore(card, newCard);
            } else {
                cardsGrid.appendChild(card);
            }
        });
        
        // Update result count indicator
        this.updateResultCountIndicator(entities.length, this.manager.count());
    }

    /**
     * Update result count indicator
     * @param {number} filteredCount - Number of filtered results
     * @param {number} totalCount - Total number of entities
     */
    updateResultCountIndicator(filteredCount, totalCount) {
        let indicator = document.querySelector('.result-count-indicator');
        
        // Remove existing indicator
        if (indicator) {
            indicator.remove();
        }
        
        // Only show if filtering is active
        if (!this.filterState.active) return;
        
        indicator = document.createElement('div');
        indicator.className = 'result-count-indicator';
        indicator.textContent = `Showing ${filteredCount} of ${totalCount} ${this.entityNamePlural}`;
        
        const container = document.querySelector(`#${this.entityName}-container`);
        if (container) {
            const cardsGrid = document.getElementById(`${this.entityName}-cards-grid`);
            if (cardsGrid) {
                container.insertBefore(indicator, cardsGrid);
            }
        }
    }


    /**
     * Refresh current UI after data changes
     * Updates the current view without navigation
     */
    async refreshUI() {
        try {
            this.logger.ui('Refreshing current UI');
            
            // Get current container
            const contentContainer = document.getElementById("content-container");
            if (!contentContainer) {
                this.logger.warn('No content container found for UI refresh');
                return;
            }
            
            // Check what type of view is currently displayed
            const isDetailsView = contentContainer.querySelector(`.${this.entityName}-details`);
            
            if (isDetailsView && this.current_entity_id) {
                // Refresh details view
                const newDetails = await this.displayEntityDetails(this.current_entity_id);
                contentContainer.innerHTML = '';
                contentContainer.appendChild(newDetails);
            } else {
                // Refresh summary/dashboard view
                const newSummary = await this.displayEntitySummary();
                contentContainer.innerHTML = '';
                contentContainer.appendChild(newSummary);
            }
            
            this.logger.success('UI refreshed successfully');
            
        } catch (error) {
            this.logger.error('Failed to refresh UI:', error);
            this.errorHandler('refresh', error);
        }
    }

    // ===============================
    // SCHEMA-DRIVEN FORM DATA COLLECTION
    // ===============================

    /**
     * Collect form data automatically based on schema
     * Template method pattern: Subclasses override collectCustomFormData() to add custom fields
     * @returns {Object} Collected and processed form data
     */
    collectFormData() {
        try {
            const data = { id: this.current_entity_id };
            const editableFields = this.getEditableFields();

            this.logger.debug(`Collecting form data for fields: ${editableFields.join(', ')}`);

            // Collect all editable fields from the form
            editableFields.forEach(fieldName => {
                const fieldElement = document.getElementById(`${this.entityName}-edit-${fieldName}`);
                if (fieldElement) {
                    if(fieldElement.type == "checkbox"){
                        data[fieldName] = fieldElement.checked;
                    } else {
                        data[fieldName] = fieldElement.value;
                    }
                } else {
                    this.logger.warn(`Form field not found: ${this.entityName}-edit-${fieldName}`);
                }
            });

            this.logger.debug('Base form data collected:', data);

            // Hook for subclasses to add custom fields
            const customData = this.collectCustomFormData();

            // Merge base data with custom data
            const mergedData = { ...data, ...customData };

            this.logger.debug('Merged form data (base + custom):', mergedData);

            // Process the form data through Manager for type conversion and validation
            const processedData = this.manager.processFormData(mergedData);
            this.logger.debug('Processed form data:', processedData);

            return processedData;
        } catch (error) {
            this.logger.error('Error collecting form data:', error);
            this.showError(`Error collecting form data: ${error.message}`);
            throw error;
        }
    }

    /**
     * Hook for subclasses to collect custom form data
     * Override this method to add entity-specific fields
     * @returns {Object} Custom form data (default: empty object)
     */
    collectCustomFormData() {
        // Default implementation: no custom data
        return {};
    }

    /**
     * Validate form data before save/create
     * Template method pattern with cleanup
     * @param {Object} formData - Form data to validate
     * @returns {boolean} True if valid, false if not
     */
    validateFormData(formData) {
        // Create temp entity for validation
        const tempEntity = this.manager.create(formData);
        const validation = this.manager.validate(tempEntity);

        // Always cleanup temp entity
        this.manager.delete(tempEntity.id);

        if (!validation.isValid) {
            this.showValidationErrors(validation.errors);
            return false;
        }

        return true;
    }

    /**
     * Prepare API data from form data
     * Override this method in subclasses to customize API payload
     * @param {Object} formData - Collected form data
     * @param {boolean} isCreate - True if creating, false if updating
     * @returns {Object} API-ready data
     */
    prepareApiData(formData, isCreate = false) {
        // Default: use form data as-is
        // Subclasses can override to transform data for API
        return formData;
    }

    // ===============================
    // DISPLAY REFRESH METHODS
    // ===============================

    /**
     * Refresh card display by reloading entities
     * Override getCardsGridId() to customize grid selector
     * @returns {Promise<void>}
     */
    async refreshCardDisplay() {
        const gridId = this.getCardsGridId();
        const cardsGrid = document.getElementById(gridId);

        if (!cardsGrid) {
            this.logger.warn(`${this.entityName} cards container not found - not in display view`);
            return;
        }

        try {
            // Clear existing entity cards (preserve "new entity" card)
            this.clearExistingCards(cardsGrid);

            // Reload entities
            const entities = await this.loadEntities();

            // Render new cards
            await this.renderEntityCards(cardsGrid, entities);

            this.logger.success(`Refreshed display with ${entities.length} ${this.entityNamePlural}`);
        } catch (error) {
            this.logger.error('Failed to refresh display:', error);
            this.errorHandler('refresh', error);
        }
    }

    /**
     * Get cards grid container ID
     * Override to customize grid selector
     * @returns {string} Grid element ID
     */
    getCardsGridId() {
        return `${this.entityName}-cards-grid`;
    }

    /**
     * Clear existing entity cards from grid
     * Override to customize which cards to preserve
     * @param {HTMLElement} cardsGrid - Cards container element
     */
    clearExistingCards(cardsGrid) {
        // Default: Remove all entity cards except "new entity" card
        const selectors = [
            `.${this.entityName}-card:not(.entity-card-new)`,
            `.${this.entityName}-card:not(.${this.entityName}-card-new-${this.entityName})`
        ];

        selectors.forEach(selector => {
            const cards = cardsGrid.querySelectorAll(selector);
            cards.forEach(card => card.remove());
        });
    }

    /**
     * Render entity cards into grid
     * Override to customize card rendering
     * @param {HTMLElement} cardsGrid - Cards container element
     * @param {Array} entities - Entities to render
     */
    async renderEntityCards(cardsGrid, entities) {
        for (const entity of entities) {
            const cardData = this.prepareCardData(entity);
            const card = this.views.createEntityCard(cardData);

            // Insert before "new entity" card if it exists
            const newCard = cardsGrid.querySelector('.entity-card-new') ||
                           cardsGrid.querySelector(`.${this.entityName}-card-new-${this.entityName}`);

            if (newCard) {
                cardsGrid.insertBefore(card, newCard);
            } else {
                cardsGrid.appendChild(card);
            }
        }
    }

    // ===============================
    // CONTEXT DATA MANAGEMENT
    // ===============================

    /**
     * Update context data with new values
     * Only updates fields that are provided in newContextData
     * @param {Object} newContextData - New context values
     * @param {Array<string>} fields - Fields to update (default: common fields)
     */
    updateContextData(newContextData, fields = ['dogs', 'activities', 'users', 'currentDogId', 'currentActivityId', 'currentUserId']) {
        if (!this.contextData) {
            this.contextData = {};
        }

        fields.forEach(field => {
            if (newContextData.hasOwnProperty(field)) {
                // For arrays, only update if it's actually an array
                if (Array.isArray(newContextData[field])) {
                    this.contextData[field] = newContextData[field];
                }
                // For other values, update if truthy or explicitly null/false
                else if (newContextData[field] !== undefined) {
                    this.contextData[field] = newContextData[field];
                }
            }
        });

        this.logger.debug('Context data updated:', this.contextData);
    }

    // ===============================
    // SHARE FORM COLLECTION
    // ===============================

    /**
     * Collect share form data
     * Generic method for collecting share form fields
     * @param {Array<Object>} fields - Array of field definitions [{id, name}, ...]
     * @returns {Object} Collected share form data
     */
    collectShareFormData(fields) {
        const data = {};

        fields.forEach(field => {
            const element = document.getElementById(field.id);
            if (element) {
                data[field.name] = element.value || '';
            } else {
                this.logger.warn(`Element with ID '${field.id}' not found`);
                data[field.name] = '';
            }
        });

        // Add entity ID
        data[`${this.entityName}_id`] = this.current_entity_id;

        return data;
    }

    /**
     * Validate form field using schema
     * @param {HTMLElement} field - Field element to validate
     */
    validateFormField(field) {
        const fieldName = field.name;
        const fieldSchema = this.manager.getFieldSchema(fieldName);
        
        if (!fieldSchema) {
            this.logger.warn(`No schema found for field: ${fieldName}`);
            return;
        }

        // Basic HTML5 validation
        const isValidHTML5 = field.checkValidity();
        
        // Schema-based validation
        let isValidSchema = true;
        let customErrorMessage = '';

        try {
            // Convert and validate the value
            const convertedValue = this.manager.convertFieldValue(field.value, fieldName);
            
            // Create a temporary object for validation
            const tempObj = { [fieldName]: convertedValue };
            const validation = this.manager.validate(tempObj);
            
            if (!validation.isValid) {
                isValidSchema = false;
                customErrorMessage = validation.errors.join(', ');
            }
        } catch (error) {
            isValidSchema = false;
            customErrorMessage = error.message;
        }

        // Apply validation results
        const isValid = isValidHTML5 && isValidSchema;

        // Check if field is empty
        const isEmpty = field.value === '' || field.value === null || field.value === undefined;
        const isRequired = fieldSchema.required === true;

        // Empty optional fields should stay grey (no class applied)
        if (isEmpty && !isRequired) {
            field.classList.remove('valid', 'invalid');
        } else {
            // For all other cases, apply valid/invalid classes
            field.classList.toggle('invalid', !isValid);
            field.classList.toggle('valid', isValid);
        }

        if (!isValid && customErrorMessage) {
            field.setCustomValidity(customErrorMessage);
        } else {
            field.setCustomValidity('');
        }

        this.logger.debug(`Validated field ${fieldName}: ${isValid ? 'valid' : 'invalid'}`);
    }

    // ===============================
    // EVENT LISTENERS - CENTRALIZED SYSTEM
    // ===============================

    /**
     * Main event listener setup - delegates to specialized creator methods
     * Override individual creator methods in subclasses for custom behavior
     */
    createEventListeners() {
        this.logger.debug('Setting up event listeners');

        this.createClickListener();
        this.createChangeListener();
        this.createInputListener();
        this.createSubmitListener();

        this.logger.debug('Event listeners registered');
    }

    /**
     * Create click event listener with common patterns
     * Override in subclasses to add entity-specific click handling
     */
    createClickListener() {
        document.addEventListener('click', async (e) => {
            await this.handleClickEvents(e);
        });
    }

    /**
     * Create change event listener with common patterns
     * Override in subclasses to add entity-specific change handling
     */
    createChangeListener() {
        document.addEventListener('change', async (e) => {
            await this.handleChangeEvents(e);
        });
    }

    /**
     * Create input event listener with common patterns
     * Override in subclasses to add entity-specific input handling
     */
    createInputListener() {
        document.addEventListener('input', async (e) => {
            await this.handleInputEvents(e);
        });
    }

    /**
     * Create submit event listener with common patterns
     * Override in subclasses to add entity-specific submit handling
     */
    createSubmitListener() {
        document.addEventListener('submit', async (e) => {
            await this.handleSubmitEvents(e);
        });
    }

    /**
     * Handle all click events - orchestrates common click patterns
     * Calls specialized methods in order, early returns when handled
     * Override individual handler methods for custom behavior
     */
    async handleClickEvents(e) {
        const target = e.target;

        // 1. Entity card clicks (view entity)
        if (await this.handleEntityCardClick(target, e)) return;

        // 2. New entity button clicks (create new)
        if (await this.handleNewEntityClick(target, e)) return;

        // 3. Action button clicks (edit, delete, share, etc.)
        if (await this.handleActionButtonClick(target, e)) return;

        // 4. Form button clicks (save, cancel, etc.)
        if (await this.handleFormButtonClick(target, e)) return;

        // 5. Filter button clicks (clear, apply)
        if (await this.handleFilterButtonClick(target, e)) return;
    }

    /**
     * Handle entity card click - navigates to entity details
     * Override to customize card click behavior
     */
    async handleEntityCardClick(target, e) {
        const entityCard = target.closest(`.${this.entityName}-card`);
        if (entityCard && !target.closest('.action-button')) {
            this.handleCardClick(entityCard.id);
            return true;
        }
        return false;
    }

    /**
     * Handle new entity button click - opens create form
     * Override to customize new entity creation
     */
    async handleNewEntityClick(target, e) {
        const newEntityCard = target.closest(`.${this.entityName}-new-entity-button`);
        if (newEntityCard) {
            this.logger.debug('New entity card clicked');
            await this.handleCreateNewEntity();
            return true;
        }
        return false;
    }

    /**
     * Handle action button click - executes entity actions
     * Override getActionHandlers() to add custom actions
     */
    async handleActionButtonClick(target, e) {
        const actionButton = target.closest('.action-button');
        if (actionButton) {
            const action = actionButton.getAttribute('data-action');
            const entityId = actionButton.getAttribute(`data-${this.entityName}-id`);

            // Only handle if we found OUR entity ID
            if (!entityId) {
                this.logger.debug(`No ${this.entityName} ID found, letting other controllers handle`);
                return false;
            }

            this.logger.debug(`Action button clicked: ${action} on ${this.entityName} ${entityId}`);

            if (action && this.actionHandlers[action]) {
                await this.actionHandlers[action](entityId, actionButton);
                e.stopPropagation();
                return true;
            } else {
                this.logger.warn(`No handler found for action: ${action}`);
            }
        }
        return false;
    }

    /**
     * Handle form button click - save, cancel, create, etc.
     * Override getFormButtonMappings() to add custom form buttons
     */
    async handleFormButtonClick(target, e) {
        // CRITICAL FIX: Only handle if this button belongs to OUR entity type
        // Check if button ID starts with our entity name (e.g., 'dog-edit-create' for dogController)
        if (target.id && !target.id.startsWith(this.entityName + '-')) {
            // This button belongs to a different entity controller
            return false;
        }

        const buttonMappings = this.getFormButtonMappings();
        const buttonAction = buttonMappings[target.id];

        if (buttonAction) {
            this.logger.debug(`Form button clicked: ${target.id}`);
            await buttonAction();
            return true;
        }
        return false;
    }

    /**
     * Get form button mappings - override to add custom buttons
     * @returns {Object} Map of button IDs to handler functions
     */
    getFormButtonMappings() {
        return {
            [`${this.entityName}-edit-save`]: () => this.handleSaveEntity(),
            [`${this.entityName}-edit-cancel`]: () => this.overlay.closeOverlay(),
            [`${this.entityName}-edit-create`]: () => this.handleCreateEntity(),
            [`${this.entityName}-edit-abort`]: () => this.handleAbortCreate(),
            [`${this.entityName}-share-submit`]: () => this.handleShareEntity(),
            [`${this.entityName}-share-abort`]: () => this.overlay.closeOverlay(),
        };
    }

    /**
     * Handle filter button click - clear, apply, toggle
     * Override to customize filter button behavior
     */
    async handleFilterButtonClick(target, e) {
        if (target.id === 'clear-filters-btn' || target.closest('#clear-filters-btn')) {
            this.logger.debug('Clear filters button clicked');
            this.clearAllFilters();
            return true;
        }

        if (target.id === 'apply-filters-btn' || target.closest('#apply-filters-btn')) {
            this.logger.debug('Apply filters button clicked');
            const filteredEntities = this.applyFilters();
            this.displayFilteredResults(filteredEntities);
            this.updateFilterIndicators();
            return true;
        }

        // Handle filter toggle button
        if (target.id === 'filter-toggle-btn' || target.closest('#filter-toggle-btn')) {
            this.logger.debug('Filter toggle button clicked');
            this.toggleFiltersCollapse();
            return true;
        }

        // Handle filter tag removal (X button on individual filter tags)
        const filterTag = target.closest('.filter-tag');
        if (filterTag) {
            const filterName = filterTag.dataset.filterName;
            this.logger.debug(`Filter tag remove clicked: ${filterName}`);
            this.removeFilter(filterName);
            return true;
        }

        return false;
    }

    /**
     * Handle all change events - orchestrates common change patterns
     * Calls specialized methods in order, early returns when handled
     */
    async handleChangeEvents(e) {
        const target = e.target;

        // 1. File input changes
        if (await this.handleFileInputChange(target, e)) return;

        // 2. Filter field changes
        if (await this.handleFilterFieldChange(target, e)) return;

        // 3. Form field validation
        if (await this.handleFormFieldChange(target, e)) return;
    }

    /**
     * Handle file input change
     * Override to customize file handling
     */
    async handleFileInputChange(target, e) {
        if (target.classList.contains('file-field-input')) {
            this.logger.debug('File input changed');
            await this.handleFileChange(target);
            return true;
        }
        return false;
    }

    /**
     * Handle filter field change
     * Override to customize filter behavior
     */
    async handleFilterFieldChange(target, e) {
        if (target.closest('.entity-filter-container')) {
            this.logger.debug('Filter changed', { field: target.name, value: target.value });
            await this.handleFilterChange(target);
            return true;
        }
        return false;
    }

    /**
     * Handle form field change (validation)
     * Override to customize form field validation
     */
    async handleFormFieldChange(target, e) {
        if (target.form && target.form.classList.contains(`${this.entityName}-form`)) {
            this.validateFormField(target);
            return true;
        }
        return false;
    }

    /**
     * Handle all input events - orchestrates common input patterns
     * Calls specialized methods in order, early returns when handled
     */
    async handleInputEvents(e) {
        const target = e.target;

        // 1. Search input
        if (await this.handleSearchInputChange(target, e)) return;

        // 2. Form field validation (real-time)
        if (await this.handleFormInputValidation(target, e)) return;
    }

    /**
     * Handle search input change
     * Override to customize search behavior
     */
    async handleSearchInputChange(target, e) {
        if (target.id === `${this.entityName}-search`) {
            this.logger.debug(`Search input: "${target.value}"`);
            await this.handleSearchInput(target.value, target);
            return true;
        }
        return false;
    }

    /**
     * Handle form input validation (real-time)
     * Override to customize validation behavior
     */
    async handleFormInputValidation(target, e) {
        // Only validate if this is OUR entity's form
        if (target.form && target.form.classList.contains(`${this.entityName}-form`)) {
            this.validateFormField(target);
            return true;
        }

        // More specific real-time validation check
        if (target.hasAttribute('data-validate-realtime') &&
            target.closest(`.${this.entityName}-form`)) {
            this.validateFormField(target);
            return true;
        }

        return false;
    }

    /**
     * Handle all submit events
     * Override to customize form submission
     */
    async handleSubmitEvents(e) {
        if (e.target.classList.contains(`${this.entityName}-form`)) {
            e.preventDefault();
            this.logger.debug('Form submitted');
            await this.handleFormSubmit(e.target);
            return true;
        }
        return false;
    }

    get actionHandlers() {
        return {
            'close': (entityId) => this.overlay.closeOverlay(),
            'edit': (entityId) => this.handleEditEntity(entityId),
            'copy': (entityId) => this.handleCopyEntity(entityId),
            'delete': (entityId) => this.handleDeleteEntity(entityId, true),
            'print': (entityId) => this.handlePrintEntity(entityId),
            'share': (entityId) => this.handleShareEntityAction(entityId),
            'unlink': (entityId) => this.handleUnlinkEntity(entityId),
            'view': (entityId) => this.displayEntityDetails(entityId, true), // Add this
            'download': (entityId) => this.handleDownloadEntity(entityId),
            'unshare': (entityId, actionButton) => this.handleUnshareEntity(entityId, actionButton),
            'restore': (entityId) => this.handleRestoreEntity(entityId),
        };
    }

    // ===============================
    // FILTER EVENT HANDLERS
    // ===============================

    /**
     * Handle search input changes
     * @param {string} searchValue - Search input value
     * @param {HTMLElement} inputElement - Input element
     */
    async handleSearchInput(searchValue, inputElement) {
        // Update filter state
        this.filterState.values.search = searchValue;
        
        // Apply filters based on mode
        if (this.filterSettings.realtime) {
            // Debounce real-time filtering
            clearTimeout(this.searchTimeout);
            this.searchTimeout = setTimeout(() => {
                const filteredEntities = this.applyFilters();
                this.displayFilteredResults(filteredEntities);
                this.updateFilterIndicators();
            }, AppSettings.ui.realtimeDebounce);
        } else {
            // Just update indicators for apply-button mode
            this.updateFilterIndicators();
        }
    }

    /**
     * Handle filter field changes
     * @param {HTMLElement} filterElement - Filter input element
     */
    async handleFilterChange(filterElement) {
        const filterName = filterElement.name || filterElement.id.replace(`${this.entityName}-`, '').replace('-filter', '');
        const filterValue = filterElement.value;

        this.logger.debug(`Filter change: ${filterName} = "${filterValue}"`);

        // Update filter state
        this.filterState.values[filterName] = filterValue;

        // Update filter tags
        this.updateFilterTags();

        // Apply filters based on mode
        if (this.filterSettings.realtime) {
            const filteredEntities = this.applyFilters();
            this.displayFilteredResults(filteredEntities);
            this.updateFilterIndicators();
        } else {
            // Just update indicators for apply-button mode
            this.updateFilterIndicators();
        }
    }

    // ===============================
    // API METHODS WITH BETTER ERROR HANDLING
    // ===============================

    async fetchEntities(params = {}) {
        try {
            this.setLoadingState('fetchEntities', true);
            
            const queryString = new URLSearchParams(params).toString();
            const endpoint = `/api/${this.entityNamePlural}${queryString ? `?${queryString}` : ''}`;
            
            this.logger.api(`Fetching entities from ${endpoint}`);
            const data = await this.api.apiCall(endpoint, "GET");
            
            const entities = data.data || [];
            this.logger.api(`Fetched ${entities.length} entities`);
            return entities;
        } catch (error) {
            this.logger.error('Failed to fetch entities:', error);
            this.errorHandler('fetch', error);
            return [];
        } finally {
            this.setLoadingState('fetchEntities', false);
        }
    }

    async fetchEntityById(entityId) {
        try {
            this.setLoadingState(`fetch_${entityId}`, true);
            
            this.logger.api(`Fetching entity by ID: ${entityId}`);
            const data = await this.api.apiCall(`/api/${this.entityNamePlural}/${entityId}`, "GET");
            
            this.logger.api(`Fetched entity: ${entityId}`);
            return data.data;
        } catch (error) {
            this.logger.error(`Failed to fetch entity ${entityId}:`, error);
            this.errorHandler('fetchById', error, entityId);
            return null;
        } finally {
            this.setLoadingState(`fetch_${entityId}`, false);
        }
    }

    async createEntityAPI(entityData) {
        try {
            this.setLoadingState('create', true);
            
            this.logger.api('Creating entity via API', { id: entityData.id });
            const response = await this.api.apiCall(`/api/${this.entityNamePlural}`, "POST", entityData);

            this.logger.success('Entity created successfully', { id: response.data?.id });
            return response.data;
        } catch (error) {
            this.logger.error('Failed to create entity:', error);
            this.handleAPIError('create', error, entityData);
            return null;
        } finally {
            this.setLoadingState('create', false);
        }
    }

    async updateEntityAPI(entityId, entityData) {
        try {
            this.setLoadingState('update', true);
            
            this.logger.api(`Updating entity via API: ${entityId}`);
            const response = await this.api.apiCall(`/api/${this.entityNamePlural}/${entityId}`, "PUT", entityData);
            
            this.logger.success(`Entity updated successfully: ${entityId}`);
            return response.data;
        } catch (error) {
            this.logger.error(`Failed to update entity ${entityId}:`, error);
            this.handleAPIError('update', error, entityId);
            return null;
        } finally {
            this.setLoadingState('update', false);
        }
    }

    async deleteEntityAPI(entityId) {
        try {
            this.setLoadingState('delete', true);
            
            this.logger.api(`Deleting entity via API: ${entityId}`);
            const response = await this.api.apiCall(`/api/${this.entityNamePlural}/${entityId}`, "DELETE");
            
            const success = response.status === 200;
            if (success) {
                this.logger.success(`Entity deleted successfully: ${entityId}`);
            } else {
                this.logger.warn(`Delete request returned status: ${response.status}`);
            }
            
            return success;
        } catch (error) {
            this.logger.error(`Failed to delete entity ${entityId}:`, error);
            this.errorHandler('delete', error, entityId);
            return false;
        } finally {
            this.setLoadingState('delete', false);
        }
    }

    /**
     * Handle API errors with detailed error message extraction
     */
    handleAPIError(operation, error, context) {
        let errorMessage = `Error in ${operation}`;
        let details = [];

        // Extract detailed error information
        if (error.response && error.response.data) {
            const responseData = error.response.data;
            
            if (responseData.error) {
                errorMessage = responseData.error;
            }
            
            if (responseData.details) {
                if (Array.isArray(responseData.details)) {
                    details = responseData.details;
                } else {
                    details = [responseData.details];
                }
            }
            
            if (responseData.message && responseData.message !== responseData.error) {
                errorMessage = responseData.message;
            }
        } else if (error.message) {
            errorMessage = error.message;
        }

        // Log the full error for debugging
        this.logger.error(`API Error in ${operation}:`, {
            message: errorMessage,
            details: details,
            context: context,
            fullError: error
        });

        // Show user-friendly error message using BaseMessages
        if (details.length > 0) {
            baseMessages.error(`${errorMessage}: ${details.join(', ')}`);
        } else {
            baseMessages.error(errorMessage);
        }
    }

    // ===============================
    // ENTITY LOADING WITH CACHING
    // ===============================

    async loadEntities(forceRefresh = false) {
        this.logger.process(`Loading entities (force: ${forceRefresh})`);
        
        if (forceRefresh) {
            this.manager.resetCacheState();
        }
        
        const needsFullRefresh = this.manager.needsFullDatasetRefresh();
        
        if (!needsFullRefresh && this.manager.count() > 0) {
            this.logger.cache(`Using cached full dataset (${this.manager.count()} entities)`);
            return this.manager.read();
        }

        this.logger.process('Loading ALL entities from API');
        
        const entities = await this.fetchEntities();
        if (entities.length > 0) {
            this.manager.clear();
            const entityObjects = this.manager.createBulk(entities);
            
            this.manager.markFullDatasetLoaded();
            
            await this.postProcessEntities(entityObjects);
            
            return entityObjects;
        }
        
        this.manager.markFullDatasetLoaded();
        return [];
    }

    async loadEntityById(entityId, forceRefresh = false) {
        this.logger.process(`Loading entity by ID: ${entityId}`);
        
        if (!forceRefresh) {
            const cached = this.manager.read(entityId);
            if (cached) {
                this.logger.cache('Using cached entity');
                await this.postProcessEntity(cached);
                return cached;
            }
        }

        const entityData = await this.fetchEntityById(entityId);
        if (entityData) {
            const entityObject = this.manager.create(entityData);
            
            this.manager.markEntityLoaded(entityId);
            
            await this.postProcessEntity(entityObject);
            return entityObject;
        }
        
        return null;
    }

    // Override in subclasses for entity-specific processing
    async postProcessEntity(entity) {
        this.logger.debug(`Default post-processing for: ${this.manager.getDisplayName(entity)}`);
    }

    async postProcessEntities(entities) {
        this.logger.process(`Post-processing ${entities.length} entities...`);
        
        for (const entity of entities) {
            await this.postProcessEntity(entity);
        }
        
        this.logger.success(`Finished post-processing ${entities.length} entities`);
    }

    // ===============================
    // DISPLAY METHODS
    // ===============================

    /**
     * Display entity summary - NOW RETURNS container instead of modifying DOM
     * @returns {HTMLElement} Entity container with cards and filters
     */
    async displayEntitySummary(entities = null, options = {}) {
        this.logger.ui('Displaying entity cards');

        try {
            // Pass title option to container creation
            const container = await this.views.createEntityContainer(options);

            // Get header container (created by base createEntityContainer)
            let containerHeader = container.querySelector('.entity-container-header');

            // Only add filter container if not already present (some views add it in createEntityContainer)
            if (!container.querySelector('.entity-filter-container')) {
                const filterFields = this.getFilterFields();
                const filterContainer = this.views.createEntityFilterContainer(filterFields, this.filterSettings);
                if (containerHeader) {
                    containerHeader.appendChild(filterContainer);
                } else {
                    // Fallback to appending directly to container if no header exists
                    container.appendChild(filterContainer);
                }
            }

            // Add new entity button to header if it exists
            if (containerHeader && !containerHeader.querySelector('.new-entity-button')) {
                const newEntityButton = this.views.createNewEntityButton();
                containerHeader.appendChild(newEntityButton);
            }

            // Only add cards container if not already present
            let cardsContainer = container.querySelector('.entity-cards-container');
            if (!cardsContainer) {
                cardsContainer = this.views.createEntityCardsContainer();
                container.appendChild(cardsContainer);
            }

            if(entities == null){
                entities = await this.loadEntities();
            }

            this.logger.ui(`Rendering ${entities.length} entity cards`);

            for (const entity of entities) {
                const cardData = this.prepareCardData(entity);
                const card = this.views.createEntityCard(cardData);
                cardsContainer.appendChild(card);
            }

            this.logger.success('Entity cards displayed successfully');
            return container;

        } catch (error) {
            this.logger.error('Failed to display entity cards:', error);
            this.errorHandler('display', error);
            throw error;
        }
    }


    /**
     * Display entity details - NOW RETURNS details element instead of modifying DOM
     * @param {string} entityId - Entity ID
     * @param {boolean} preventRoute - Whether to prevent routing (default: false)
     * @returns {HTMLElement} Entity details element
     */
    async displayEntityDetails(entityId, preventRoute = false) {
        this.logger.ui(`Displaying entity details: ${entityId}`);
        this.current_entity_id = entityId;

        try {
            const entity = await this.loadEntityById(entityId);
            
            if (entity) {
                const detailsData = this.prepareDetailsData(entity);
                const details = this.views.createEntityDetails(detailsData);
                this.logger.success(`Entity details displayed: ${this.manager.getDisplayName(entity)}`);
                return details;
            } else {
                this.logger.warn(`Entity not found: ${entityId}`);
                const errorElement = document.createElement('div');
                errorElement.innerHTML = `<h1>${this.views.formatEntityName()} not found.</h1>`;
                return errorElement;
            }
        } catch (error) {
            this.logger.error(`Failed to display entity details for ${entityId}:`, error);
            this.errorHandler('displayDetails', error, entityId);
            
            const errorElement = document.createElement('div');
            errorElement.innerHTML = `<h1>Error loading ${this.entityName} details.</h1>`;
            return errorElement;
        }
    }


    async loadEntitiesBySubject(subjectType, subjectId) {
        // Check if we need to fetch data for this subject
        const shouldFetch = this.manager.shouldFetchBySubject(subjectType, subjectId);

        if (!shouldFetch) {
            // Data already loaded - retrieve from cache
            const cachedEntities = this.manager.getEntitiesBySubject(subjectType, subjectId);
            this.logger.cache(`Using cached data for ${subjectType}:${subjectId}: ${cachedEntities.length} entities`);
            return cachedEntities;
        }

        // Fetch from API
        this.logger.process(`Fetching entities from API for ${subjectType}:${subjectId}`);
        const entities = await this.fetchEntitiesBySubject(subjectType, subjectId);

        if (entities.length > 0) {
            // Add to shared cache
            const entityObjects = this.manager.createBulk(entities);
            // Mark this subject as loaded
            this.manager.markSubjectLoaded(subjectType, subjectId);
            this.logger.success(`Loaded ${entityObjects.length} entities for ${subjectType}:${subjectId}`);
            return entityObjects;
        } else {
            // Even if no entities found, mark as loaded to avoid re-fetching
            this.manager.markSubjectLoaded(subjectType, subjectId);
            this.logger.warn(`No entities found for ${subjectType}:${subjectId}`);
            return [];
        }
    }



    /**
     * Fetch entities by subject (generic version)
     * @param {string} subject_type - Subject type (e.g., 'dog', 'user')
     * @param {string} subject_id - Subject ID
     * @returns {Array} Array of entities
     */
    async fetchEntitiesBySubject(subject_type, subject_id) {
        try {
            this.setLoadingState(`fetchBySubject_${subject_type}_${subject_id}`, true);
            
            this.logger.api(`Fetching ${this.entityNamePlural} for ${subject_type} ${subject_id}`);
            const data = await this.api.apiCall(`/api/${this.entityNamePlural}/subject/${subject_type}/${subject_id}`, "GET");
            
            const entities = data.data || [];
            this.logger.api(`Fetched ${entities.length} ${this.entityNamePlural} for ${subject_type} ${subject_id}`);
            return entities;
        } catch (error) {
            this.logger.error(`Failed to fetch ${this.entityNamePlural} for ${subject_type} ${subject_id}:`, error);
            this.errorHandler('fetchBySubject', error);
            return [];
        } finally {
            this.setLoadingState(`fetchBySubject_${subject_type}_${subject_id}`, false);
        }
    }


    // ===============================
    // CRUD HANDLERS (FIXED: Use skipValidation for placeholder entities + BaseMessages for delete)
    // ===============================
    async handleCardClick(id, preventRoute = false) {
        if (!preventRoute) {
            this.logger.debug(`Entity card clicked: ${id}`);
            const router = (await import('@/modules/core/coreRouter.js')).default;
            router.navigateTo(`#/dashboard/${this.entityNamePlural}/${this.entityName}/${id}`);
        }
        return await this.displayEntityDetails(id);
    }


    async handleCreateNewEntity() {
        try {
            this.logger.process('Handling create new entity');
            // Create placeholder entity with validation skipped for form editing
            const newEntity = this.manager.create({}, true); // skipValidation = true
            this.current_entity_id = newEntity.id;

            // IMPORTANT: Do NOT route during creation - entity doesn't exist in API yet
            // Routing will happen AFTER successful creation in handleCreateEntity()

            const formData = await this.prepareFormData(newEntity, true);
            const editView = this.views.createEntityEditView(formData);
            await this.overlay.showOverlay(editView);

            this.logger.success('Create new entity form displayed');
        } catch (error) {
            this.logger.error('Failed to handle create new entity:', error);
            this.errorHandler('createNew', error);
        }
    }

    async handleEditEntity(entityId) {
        try {
            if (entityId) {
                this.current_entity_id = entityId;
            }
            this.logger.process(`Handling edit entity: ${entityId}`);
            const currentEntity = this.manager.read(entityId);
            const formData = await this.prepareFormData(currentEntity, false);
            const editView = this.views.createEntityEditView(formData);
            await this.overlay.showOverlay(editView);

            this.logger.success('Edit entity form displayed');
        } catch (error) {
            this.logger.error('Failed to handle edit entity:', error);
            this.errorHandler('edit', error);
        }
    }

    async handleCopyEntity(entityId, route = true){
        try{
            this.logger.process(`Handling clone entity: ${entityId}`);
            let copiedEntity = this.manager.copy(entityId);

            this.current_entity_id = copiedEntity.id;

            if(route){
                const router = (await import('@/modules/core/coreRouter.js')).default;
                await router.navigateTo(`#/dashboard/${this.entityNamePlural}/${this.entityName}/${copiedEntity.id}`);
            }
            
            const formData = await this.prepareFormData(copiedEntity, true);
            const editView = this.views.createEntityEditView(formData);
            await this.overlay.showOverlay(editView);
            this.logger.process(`Edit cloned entity: ${copiedEntity.id}`);

        } catch (error){
            this.logger.error('Failed to handle clone entity:', error);
            this.errorHandler('edit', error);            
        }
    }

    /**
     * Handle save entity with optional custom loading
     * @param {boolean} customLoad - If true, skip displayEntityDetails() and return for custom handling
     */
    async handleSaveEntity(customLoad = false) {
        try {
            this.logger.process(`Handling save entity: ${this.current_entity_id}`);
            const formData = this.collectFormData();

            // Validate form data
            if (!this.validateFormData(formData)) {
                return; // Validation failed, errors already shown
            }

            // Prepare API data (hook for subclasses)
            const apiData = this.prepareApiData(formData, false);

            // Update via API
            const apiResponse = await this.updateEntityAPI(this.current_entity_id, apiData);

            if (apiResponse) {
                this.manager.update(apiResponse);
                this.overlay.closeOverlay();

                // ARCHITECTURAL FIX: Route to details page after save (if not using customLoad)
                // This ensures user sees the updated entity details after editing
                if (!customLoad) {
                    const router = (await import('@/modules/core/coreRouter.js')).default;
                    await router.navigateTo(`#/dashboard/${this.entityNamePlural}/${this.entityName}/${this.current_entity_id}`);
                }

                baseMessages.success('Entity updated successfully');
            }
        } catch (error) {
            this.logger.error('Failed to handle save entity:', error);
            this.errorHandler('save', error);
        }
    }

    /**
     * Generic unshare entity method - IMPROVED to use relationship ID and refresh UI
     * @param {string} entityId - ID of the entity being unshared
     * @param {HTMLElement} actionButton - Button element with all data
     */
    async handleUnshareEntity(entityId, actionButton) {
        try {
            // Extract relationship ID from button
            const relationshipId = actionButton.getAttribute('data-relationship-id');
            const userId = actionButton.getAttribute('data-user-id');

            this.logger.process(`Handling unshare ${this.entityName}: ${entityId} - removing relationship ${relationshipId}`);

            if (!relationshipId) {
                this.logger.error('Missing relationship ID for unshare operation');
                baseMessages.error('Invalid unshare request - missing relationship data');
                return false;
            }

            // Check if this is the current user being removed
            const isCurrentUser = (userId === this.current_user);

            // Get entity for display name
            const entity = this.manager.read(entityId);
            const entityDisplayName = entity ? this.manager.getDisplayName(entity) : this.entityName;

            // Confirm before removing access with different message for current user
            const confirmMessage = isCurrentUser
                ? `Are you sure you want to remove your own access to "${entityDisplayName}"? You will no longer be able to view or edit this ${this.entityName}.`
                : `Are you sure you want to remove access to "${entityDisplayName}"? The user will no longer be able to view or edit this ${this.entityName}.`;

            const confirmUnshare = await this.showConfirmation({
                title: isCurrentUser ? 'Remove Your Access' : 'Remove Access',
                message: confirmMessage,
                confirmText: 'Remove Access',
                cancelText: 'Cancel',
                type: 'warning'
            });

            if (!confirmUnshare) {
                this.logger.debug('Unshare cancelled by user');
                return false;
            }

            // Delete relationship via API through the entity's unshare endpoint
            const response = await this.api.apiCall(`/api/${this.entityNamePlural}/${entityId}/unshare/${relationshipId}`, "DELETE");

            this.logger.debug('Unshare API response', { status: response.status, isCurrentUser });

            if (response.status === 200) {
                baseMessages.success('User access removed successfully');
                this.logger.success(`Unshare successful: removed relationship ${relationshipId}`);

                // Different behavior based on who was removed
                if (isCurrentUser) {
                    // User removed own permission - navigate back to entity overview
                    this.manager.delete(entityId);
                    const router = (await import('@/modules/core/coreRouter.js')).default;
                    router.navigateTo(`#/dashboard/${this.entityNamePlural}`);
                } else {
                    // Owner removed another user's permission - stay on page and reload
                    // Force reload from API to get updated relationships
                    const updatedEntity = await this.fetchEntityById(entityId);
                    if (updatedEntity) {
                        // Update the entity in manager with fresh data
                        this.manager.update(updatedEntity);
                    }
                    // Refresh the UI to show updated relationships
                    await this.refreshUI();
                }

                return true;
            } else {
                this.logger.error('Error removing relationship:', response);
                baseMessages.error('Error removing user access');
                return false;
            }

        } catch (error) {
            this.logger.error('Error in handleUnshareEntity:', error);
            baseMessages.error(`Error removing user access: ${error.message}`);
            return false;
        }
    }



    /**
     * Handle create entity with optional custom loading
     * @param {boolean} customLoad - If true, skip displayEntityDetails() and return for custom handling
     */
    async handleCreateEntity(customLoad = false) {
        try {
            this.logger.process(`Handling create entity: ${this.current_entity_id}`);
            const formData = this.collectFormData();

            // Validate form data
            if (!this.validateFormData(formData)) {
                return; // Validation failed, errors already shown
            }

            // Prepare API data (hook for subclasses)
            const apiData = this.prepareApiData(formData, true);

            // Create via API
            const createdEntity = await this.createEntityAPI(apiData);

            if (createdEntity) {
                // Remove the placeholder entity first
                this.manager.delete(this.current_entity_id);

                // Add the real created entity
                createdEntity._isNew = false;
                this.manager.create(createdEntity);
                this.current_entity_id = createdEntity.id;

                this.overlay.closeOverlay();

                

                // ARCHITECTURAL FIX: Route to details page AFTER successful creation (not during)
                // Only route if customLoad is false (some modules like result/note skip routing)
                if (!customLoad) {
                    const router = (await import('@/modules/core/coreRouter.js')).default;
                    await router.navigateTo(`#/dashboard/${this.entityNamePlural}/${this.entityName}/${createdEntity.id}`);
                }

                baseMessages.success('Entity created successfully');
            }
        } catch (error) {
            this.logger.error('Failed to handle create entity:', error);
            this.errorHandler('saveNew', error);
        }
    }

    /**
     * FIXED: Handle delete entity - Navigate and wait for route to complete
     */
    async handleDeleteEntity(entityId, route = true) {
        try {
            this.logger.process(`Handling delete entity: ${entityId}`);
            const entity = this.manager.read(entityId);
            const entityDisplayName = this.manager.getDisplayName(entity);

            const confirmDelete = await this.confirmDelete(entityDisplayName);

            if (!confirmDelete) {
                this.logger.debug('Delete cancelled by user');
                return;
            }

            const success = await this.deleteEntityAPI(entityId);

            if (success) {
                // Delete from local manager
                this.manager.delete(entityId);

                // Navigate and WAIT for route to process
                if(route){
                    const router = (await import('@/modules/core/coreRouter.js')).default;
                    await router.navigateTo(`#/dashboard/${this.entityNamePlural}`);
                }

                // Show success message after navigation completes
                baseMessages.success(`${entityDisplayName} deleted successfully`);

                this.logger.success(`Entity deleted: ${entityId}`);
            }
        } catch (error) {
            this.logger.error('Failed to handle delete entity:', error);
            this.errorHandler('delete', error);
        }
    }

    async handlePrintEntity(entityId){
        try {
           this.logger.info(`Handling print entity must be handelled custom: ${entityId}`); 
        } catch (error) {
            this.logger.error('Failed to handle print entity:', error);
            this.errorHandler('print', error);            
        }
    }

    async handleRestoreEntity(entityId){
        try {
           this.logger.info(`Handling restore entity must be handelled custom: ${entityId}`); 
        } catch (error) {
            this.logger.error('Failed to handle restore entity:', error);
            this.errorHandler('print', error);            
        }
    }
    

    async handleAbortCreate(route = true) {
        this.logger.debug('Handling abort create');
        await this.overlay.closeOverlay();
        
        // Clean up placeholder entity
        if (this.current_entity_id) {
            this.manager.delete(this.current_entity_id);
        }
        
        if(route){
            setTimeout(async () => {
                const router = (await import('@/modules/core/coreRouter.js')).default;
                router.navigateTo(`#/dashboard/${this.entityNamePlural}`);
            }, 500);
        }
    }

    // ===============================
    // DATA PREPARATION FOR VIEWS (Override in subclasses)
    // ===============================

    /**
     * Prepare card data for entity display
     * Default: Delegate to manager's schema-driven implementation
     * Override if entity needs custom card data structure
     */
    prepareCardData(entity) {
        // Delegate to manager if available (schema-driven)
        if (this.manager.prepareCardData) {
            return this.manager.prepareCardData(entity);
        }

        // Fallback implementation
        return {
            id: entity.id,
            header: {
                imageUrl: entity.photo_url,
                altText: `Photo of ${this.manager.getDisplayName(entity)}`
            },
            body: {
                title: this.manager.getDisplayName(entity),
                fields: [
                    { name: 'status', label: 'Status', value: entity.status }
                ]
            },
            actions: this.getEntityActions(entity)
        };
    }

    /**
     * Prepare details data for entity display
     * Default: Delegate to manager's schema-driven implementation
     * Override if entity needs custom details data structure
     */
    prepareDetailsData(entity) {
        // Delegate to manager if available (schema-driven)
        if (this.manager.prepareDetailsData) {
            return this.manager.prepareDetailsData(entity);
        }

        // Fallback implementation
        return {
            id: entity.id,
            header: {
                imageUrl: entity.photo_url,
                altText: `Photo of ${this.manager.getDisplayName(entity)}`
            },
            body: {
                title: {
                    main: this.manager.getDisplayName(entity)
                },
                sections: [
                    {
                        title: 'Basic Information',
                        className: 'basic-info',
                        fields: [
                            { name: 'status', label: 'Status', value: entity.status }
                        ]
                    }
                ]
            },
            actions: this.getEntityActions(entity)
        };
    }

    prepareFormData(entity, isNew = false) {
        return this.manager.prepareFormData(entity, isNew);
    }

    getEntityActions(entity) {
        return [
            { name: 'edit', icon: 'edit', title: 'Edit', entityId: entity.id },
            { name: 'delete', icon: 'delete', title: 'Delete', entityId: entity.id }
        ];
    }

    // ===============================
    // UI FEEDBACK METHODS (UPDATED: Use BaseMessages with fallback)
    // ===============================

    /**
     * Show validation errors - Still use overlay for form validation
     * @param {Array} errors - Array of error messages
     */
    showValidationErrors(errors) {
        try {
            const errorMessage = `
                <div class="validation-errors">
                    <span class="material-symbols-outlined">error</span>
                    <h3>Please fix the following errors:</h3>
                    <ul>
                        ${errors.map(error => `<li>${error}</li>`).join('')}
                    </ul>
                </div>
            `;
            
            this.overlay.showOverlay({
                title: 'Validation Error',
                content: errorMessage,
                closeButton: true,
                className: 'validation-error-overlay'
            });
        } catch (overlayError) {
            this.logger.error('Failed to show validation overlay, using BaseMessages fallback:', overlayError);
            // Fallback to BaseMessages
            const errorText = `Validation failed: ${errors.join(', ')}`;
            baseMessages.error(errorText);
            console.error('Validation Errors:', errors);
        }
    }

    /**
     * Show success message - Use BaseMessages with overlay fallback
     * @param {string} message - Success message
     */
    showSuccess(message) {
        try {
            baseMessages.success(message);
        } catch (messageError) {
            this.logger.error('Failed to show success message, using overlay fallback:', messageError);
            // Fallback to overlay
            try {
                const successContent = `
                    <div class="success-message">
                        <span class="material-symbols-outlined">check_circle</span>
                        <p>${message}</p>
                    </div>
                `;
                
                this.overlay.showOverlay({
                    title: 'Success',
                    content: successContent,
                    closeButton: true,
                    className: 'success-overlay',
                    autoClose: 3000
                });
            } catch (overlayError) {
                this.logger.error('Both BaseMessages and overlay failed, using console fallback:', overlayError);
            }
        }
    }

    /**
     * Show error message - Use BaseMessages with overlay fallback  
     * @param {string} message - Error message
     */
    showError(message) {
        try {
            baseMessages.error(message);
        } catch (messageError) {
            this.logger.error('Failed to show error message, using overlay fallback:', messageError);
            // Fallback to overlay
            try {
                const errorContent = `
                    <div class="error-message">
                        <span class="material-symbols-outlined">error</span>
                        <p>${message}</p>
                    </div>
                `;
                
                this.overlay.showOverlay({
                    title: 'Error',
                    content: errorContent,  
                    closeButton: true,
                    className: 'error-overlay'
                });
            } catch (overlayError) {
                this.logger.error('Both BaseMessages and overlay failed, using console fallback:', overlayError);
                // Close any existing overlay and use console
                try {
                    this.overlay.closeOverlay();
                } catch (closeError) {
                    this.logger.error('Failed to close overlay:', closeError);
                }
                console.error('ERROR:', message);
                alert(message); // Last resort user notification
            }
        }
    }

    // ===============================
    // UTILITY METHODS
    // ===============================

    /* Load an entity by ID and return specific field(s)
    * @param {string} entityId - ID of the entity to load
    * @param {string|Array} fields - Field name(s) to retrieve
    * @returns {Promise<*|Object|null>} Single field value, object with multiple fields, or null if entity not found
    */
    async getEntityFields(entityId, fields) {
        try {
            const entity = await this.loadEntityById(entityId);
            
            if (!entity) {
                return null;
            }
            
            // Single field - return just the value
            if (typeof fields === 'string') {
                return entity[fields];
            }
            
            // Multiple fields - return object with those fields
            if (Array.isArray(fields)) {
                const result = {};
                fields.forEach(fieldName => {
                    result[fieldName] = entity[fieldName];
                });
                return result;
            }
            
            throw new Error('Fields parameter must be a string or array of strings');
            
        } catch (error) {
            this.logger.error(`Failed to get fields for entity ${entityId}:`, error);
            throw error;
        }
    }


    setLoadingState(operation, loading) {
        this.loadingStates.set(operation, loading);
        this.logger.debug(`Loading state changed: ${operation} = ${loading}`);
        document.dispatchEvent(new CustomEvent('loadingStateChange', {
            detail: { operation, loading, controller: this.entityName }
        }));
    }

    isLoading(operation) {
        return this.loadingStates.get(operation) || false;
    }

    defaultErrorHandler(operation, error, context) {
        this.logger.error(`Controller error in ${operation}:`, error, context);
        
        // Use BaseMessages for error notifications
        try {
            baseMessages.error(`Error in ${operation}: ${error.message}`);
        } catch (messageError) {
            this.logger.error('Failed to show error via BaseMessages:', messageError);
            // Fallback to console + overlay close
            try {
                this.overlay.closeOverlay();
            } catch (closeError) {
                this.logger.error('Failed to close overlay:', closeError);
            }
            console.error(`Error in ${operation}:`, error.message);
        }
    }

    // ===============================
    // CONFIRMATION DIALOG SYSTEM
    // ===============================

    /**
     * Show confirmation dialog and wait for user response
     * @param {Object} options - Dialog options
     * @param {string} options.title - Dialog title
     * @param {string} options.message - Confirmation message
     * @param {string} options.confirmText - Confirm button text
     * @param {string} options.cancelText - Cancel button text
     * @param {string} options.type - Dialog type ('danger', 'warning', 'info')
     * @returns {Promise<boolean>} - true if confirmed, false if cancelled
     */
    async showConfirmation(options = {}) {
        return new Promise((resolve) => {
            const dialog = this.views.createConfirmationDialog(options);

            // Add event listeners
            const confirmBtn = dialog.querySelector('.confirmation-confirm');
            const cancelBtn = dialog.querySelector('.confirmation-cancel');

            const handleConfirm = () => {
                this.overlay.closeOverlay();
                resolve(true);
            };

            const handleCancel = () => {
                this.overlay.closeOverlay();
                resolve(false);
            };

            confirmBtn.addEventListener('click', handleConfirm);
            cancelBtn.addEventListener('click', handleCancel);

            // Show the dialog
            this.overlay.showOverlay(dialog);
        });
    }

    /**
     * Show delete confirmation dialog
     * @param {string} entityName - Name of entity to delete (for display)
     * @param {string} entityType - Type of entity (defaults to this.entityName)
     * @returns {Promise<boolean>} - true if confirmed, false if cancelled
     */
    async confirmDelete(entityName, entityType = null) {
        const type = entityType || this.entityName;
        return await this.showConfirmation({
            title: `Delete ${type}`,
            message: `Are you sure you want to delete "${entityName}"?`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            type: 'danger'
        });
    }

    // Placeholder methods for subclasses to override
    async handleShareEntity() { this.logger.debug('Share functionality to be implemented'); }
    async handleShareEntityAction() { this.logger.debug('Share action to be implemented'); }
    async handleUnlinkEntity() { this.logger.debug('Unlink functionality to be implemented'); }
    async handleViewEntity() { this.logger.debug('View functionality to be implemented'); }
    async handleDownloadEntity() { this.logger.debug('Download functionality to be implemented'); }
    async handleFileChange() { this.logger.debug('File change handling to be implemented'); }
    async handleFormSubmit() { this.logger.debug('Form submit handling to be implemented'); }
}

export default BaseController;